/**
 * FIREBASE'S OWN BROWSER DATABASES: emptied in place, never deleted, and checked without being created (2026-10-03,
 * Justin's stuck "getting your threads back…").
 *
 * One dependency-free leaf, read by the sign-out wipe (src/utils/siteDataWipe.ts), by the sign-in startup watch
 * (./authStartupWatch.ts), and by the Chromium probe (scripts/probe-idb-blocked-delete.mjs), so the probe runs the code
 * that ships.
 */

/** The auth database: if THIS one survives with a user in it, the next sign-in takes the legacy link branch. */
export const AUTH_DB_NAME = 'firebaseLocalStorageDb';

/**
 * FIREBASE'S OWN DATABASES ARE EMPTIED IN PLACE, NEVER DELETED (2026-10-03, Justin's stuck "getting your threads
 * back…").
 *
 * A delete waits for every other tab that holds the database open to let go, and the SDK in every other prest0 tab
 * holds these open for as long as it lives. While the delete waits, the browser queues every later OPEN of that
 * database behind it, so the next page's sign-in start (Firebase checks the database when it boots) waited until every
 * prest0 tab was closed: the page sat on its loading states with nothing to say why (proven in Chromium with
 * scripts/probe-idb-blocked-delete.mjs). An open is never blocked by other tabs' connections, so the wipe opens each
 * of these and clears every store in it: the stored user is gone, and nothing is left pending. Opened without a
 * version, an absent database would be CREATED; the upgrade that would create it is aborted, so it never is.
 * Bounded, like the deletes: a tab that holds a write open must not strand the person on a half-signed-out page.
 *
 * Every other database keeps the wipe's bounded delete (src/utils/siteDataWipe.ts).
 */
export const FIREBASE_SDK_DB_NAMES: readonly string[] = [
  AUTH_DB_NAME,
  'firebase-heartbeat-database',
  'firebase-installations-database',
  'firebase-messaging-database',
];

const INDEXED_DB_CLEAR_TIMEOUT_MS = 2_000;

export interface InPlaceClear {
  /** Every store in the database was cleared. */
  cleared: boolean;
  /** There was no such database (nothing to clear, and none was created). */
  absent: boolean;
}

export function clearIndexedDbInPlace(name: string): Promise<InPlaceClear> {
  return new Promise((resolve) => {
    let settled = false;
    let creating = false;
    const timer = setTimeout(() => {
      if (!settled) console.warn(`[firebaseDatabases] IndexedDB "${name}" could not be cleared in time`);
      settle({ cleared: false, absent: false });
    }, INDEXED_DB_CLEAR_TIMEOUT_MS);
    function settle(outcome: InPlaceClear): void {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(outcome);
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(name);
    } catch {
      settle({ cleared: false, absent: false });
      return;
    }
    // No version: an existing database opens as it is. An upgrade here means it did not exist; aborting it means the
    // database is never created (the open then fails with AbortError, answered below).
    req.onupgradeneeded = () => {
      creating = true;
      try { req.transaction?.abort(); } catch { /* the open fails either way */ }
    };
    req.onerror = (event) => {
      try { event.preventDefault(); } catch { /* nothing to prevent */ }
      settle({ cleared: false, absent: creating });
    };
    req.onsuccess = () => {
      const db = req.result;
      if (settled) { try { db.close(); } catch { /* already closed */ } return; }
      const close = () => { try { db.close(); } catch { /* already closed */ } };
      const stores = Array.from(db.objectStoreNames);
      if (stores.length === 0) {
        close();
        settle({ cleared: true, absent: false });
        return;
      }
      let tx: IDBTransaction;
      try {
        tx = db.transaction(stores, 'readwrite');
      } catch {
        close();
        settle({ cleared: false, absent: false });
        return;
      }
      for (const store of stores) {
        try { tx.objectStore(store).clear(); } catch { /* the transaction fails and says so */ }
      }
      tx.oncomplete = () => { close(); settle({ cleared: true, absent: false }); };
      tx.onerror = () => { close(); settle({ cleared: false, absent: false }); };
      tx.onabort = () => { close(); settle({ cleared: false, absent: false }); };
    };
  });
}

/**
 * Does this database open within `timeoutMs`? An open that cannot happen in that time is queued behind someone's
 * delete or upgrade. Never creates an absent database (the upgrade is aborted) and never holds one (closed at once).
 */
export function databaseOpensInTime(name: string, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => settle(false), timeoutMs);
    function settle(opens: boolean): void {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(opens);
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(name);
    } catch {
      settle(true); // no IndexedDB here: nothing can be held
      return;
    }
    req.onupgradeneeded = () => {
      // Absent: it opened in time; abort so it is never created.
      try { req.transaction?.abort(); } catch { /* the open fails either way */ }
    };
    req.onsuccess = () => {
      try { req.result.close(); } catch { /* already closed */ }
      settle(true);
    };
    req.onerror = (event) => {
      try { event.preventDefault(); } catch { /* nothing to prevent */ }
      settle(true);
    };
  });
}
